import { faClose, faLayerGroup, faPlus } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useMemo, useState } from "react"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import THEME from "../../../configuration/Theme"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { useCreateCatalogProductEntity } from "../../../hooks/useCreateCatalogProductEntity"
import { CatalogProductPupop } from "../CatalogProductPupop"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { ComboEntity } from "../../../Objects/Combo/ComboEntity"
import { useCreateCombo } from "../../../hooks/Combo/useCreateCombo"
import { addtionalMemory, comboMemory, catalogItemsMemory, productsMemory } from "../../../Objects/Memory/Memory"
import { selectionMemory } from "../../../Objects/Memory/Memory"
import { useSaveCombo } from "../../../hooks/Combo/useSaveCombo"
import { useGetCombo } from "../../../hooks/Combo/useGetCombo"
import { EComboItemType } from "../../../Enum/EComboItemType"
import { Permissions } from "../../../Enum/PermissionsType"
import { SIZE } from "../../../configuration/Sizes"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { useRemoveCombo } from "../../../hooks/Combo/useRemoveCombo"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useTranslation } from "react-i18next"
import GetSpace from "../../../utils/space"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { CanView } from "../../../components/CanView/CanView"
import { useError } from "../../../context/useErrorContext"

interface ComboPopupProps {
  combo?: ComboEntity
  title: string
  onClose: () => void
  onCopy?: () => void
}

export const ComboPopup: React.FC<ComboPopupProps> = ({ combo, title, onClose, onCopy }) => {
  const { t } = useTranslation("catalogProductEntity")

  const isCopiedDraft = combo?.id === -1

  const canEdite = !usePermission(Permissions.CanEditeCombos)

  const { setError } = useError()

  const { handleSaveCombo } = useSaveCombo()

  const { handleRemoveCombo } = useRemoveCombo()

  const { handleGetCombo } = useGetCombo()

  const {
    name,
    description,
    status,
    coast,
    categoryId,
    additionalsAllowed,
    freeAdditionalsAllowed,
    allowedSelections,
    freeAddtionalsLimit,
    tags,
    imageAssetIds,
    imageUrls,
    hasStockControl,
    stockQuantity,
    hasPreparationTime,
    preparationTimeMinutes,
    productOrderMode,
    reservationAllowedDays,
    reservationAllowedTimes,
    handleCategoryId,
    handleNameChange,
    handleCoastChange,
    handleDescriptionChange,
    handleStatusChange,
    handleFreeAddtionalsLimitChange,
    handleHasStockControlChange,
    handleHasPreparationTimeChange,
    handlePreparationTimeMinutesChange,
    handleProductOrderModeChange,
    setReservationAllowedDays,
    setReservationAllowedTimes,
    handleStockQuantityChange,
    handleCatalogProductEntity,
    handleInitialCatalogProductEntity,
    setPromotionsIds,
    setTags,
    setImageAssetIds,
    setImageUrls,
    setImagesChanged,
    setAdditionalsAllowed,
    setFreeAdditionalsAllowed,
    setAllowedSelections,
  } = useCreateCatalogProductEntity(true)

  const { items, addProductItem, addCatalogItemItem, removeItem, updateQuantity, handleInitialValue, handleSubmitForm } =
    useCreateCombo()

  const [selectedProductId, setSelectedProductId] = useState<string>("0")

  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState<string>("0")

  const productOptions = useMemo(() => {
    const comboProductIds = new Set(comboMemory.listData.map((comboData) => comboData.productId))

    return [
      { label: "Produto…", value: "0" },
      ...productsMemory.listData
        .filter((product) => !comboProductIds.has(product.id))
        .map((product) => ({
          label: product.catalogProductEntity.name,
          value: String(product.id),
        })),
    ]
  }, [productsMemory.listData, comboMemory.listData])

  const catalogItemOptions = useMemo(
    () => [
      { label: "Item complementar…", value: "0" },
      ...catalogItemsMemory.listData.map((catalogItem) => ({
        label: catalogItem.catalogProductEntity.name,
        value: String(catalogItem.id),
      })),
    ],
    [catalogItemsMemory.listData],
  )

  const getItemLabel = (itemType: number, itemId: number) => {
    if (itemType === EComboItemType.Product) {
      return productsMemory.findById(itemId)?.catalogProductEntity.name ?? `Produto #${itemId}`
    }

    return catalogItemsMemory.findById(itemId)?.catalogProductEntity.name ?? `Item complementar #${itemId}`
  }

  const getItemMeta = (itemType: number, itemId: number) => {
    const sourceItem =
      itemType === EComboItemType.Product
        ? productsMemory.findById(itemId)?.catalogProductEntity
        : catalogItemsMemory.findById(itemId)?.catalogProductEntity

    const fallbackIcon = itemType === EComboItemType.Product ? "🍽️" : "🥤"

    return {
      name: sourceItem?.name ?? getItemLabel(itemType, itemId),
      price: Number(sourceItem?.valueBase ?? 0),
      iconUrl: sourceItem?.iconUrl ?? "",
      fallbackIcon,
    }
  }

  const comboItemsTotalValue = useMemo(() => {
    return items.reduce((total, item) => {
      const meta = getItemMeta(item.itemType, item.itemId)
      return total + meta.price * item.quantity
    }, 0)
  }, [items])

  const comboSuggestedValue = useMemo(() => comboItemsTotalValue * 0.9, [comboItemsTotalValue])

  useEffect(() => {
    handleInitialValue(combo)

    handleInitialCatalogProductEntity(combo?.catalogProductEntity)

    setSelectedProductId("0")

    setSelectedCatalogItemId("0")
  }, [combo])

  const handleCopyClick = async () => {
    onCopy?.()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <div className={defaultStyle.alert}>
          <TitlePageResponsiveIconSmall
            title={title}
            icon={faLayerGroup}
            iconColor={THEME.colors.iconBGSecondaryColor}
            children={
              <div className={`${defaultStyle.ButtonsActions} flex-nowrap`}>
                {combo && (
                  <div className="w-[130px] mr-1">
                    <GenericDropDown
                      data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                      initialState={status}
                      disable={canEdite}
                      handlerFuncion={handleStatusChange}

                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.select.select" />
                  </div>
                )}

                <PopupHelpButton
                  helpKey="pages.products.combo.combopopup.1"
                  variant="global"
                  title={t("helpPopup:global.combo.title")}
                  description={t("helpPopup:global.combo.description")}
                  bullets={t("helpPopup:global.combo.bullets", { returnObjects: true }) as string[]}
                />

                <GetSpace right={5} />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"

                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.button.onClose" />
              </div>
            }
          />

          {isCopiedDraft && <CopyDraftAlert />}

          <form
            onSubmit={(event) => {
              event.preventDefault()

              const hasInvalidReservationTime = reservationAllowedTimes.some((time) => {
                const value = time.trim()

                if (!/^\d{2}:\d{2}$/.test(value)) {
                  return true
                }

                const [hours, minutes] = value.split(":").map(Number)

                return (
                  Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59
                )
              })

              if (productOrderMode !== 0 && (reservationAllowedDays.length === 0 || reservationAllowedTimes.length === 0)) {
                setError(t("general:label_invalid"), t("catalogProductEntity:label_reservation_config_required"))

                return
              }

              if (productOrderMode !== 0 && hasInvalidReservationTime) {
                setError(t("general:label_invalid"), t("catalogProductEntity:label_reservation_times_invalid"))

                return
              }

              if (Number(coast) <= 0) {
                setError(t("general:label_invalid"), t("label_product_value_must_be_greater_than_zero_combo"))

                return
              }

              const catalogProductEntity = handleCatalogProductEntity()

              const comboToSave = handleSubmitForm(catalogProductEntity)

              handleSaveCombo(
                comboToSave,
                () => {
                  onClose()
                  handleGetCombo(() => {})
                },
              )
            }}
          >
            <CatalogProductPupop
              canEdite={canEdite}
              canSelectType={false}
              categoryId={categoryId}
              typeSelected={0}
              name={name}
              coast={coast}
              hasPreparationTime={hasPreparationTime}
              preparationTimeMinutes={preparationTimeMinutes}
              productOrderMode={productOrderMode}
              reservationAllowedDays={reservationAllowedDays}
              reservationAllowedTimes={reservationAllowedTimes}
              description={description}
              freeAddtionals={freeAdditionalsAllowed}
              freeAddtionalsLimit={freeAddtionalsLimit}
              tags={tags}
              hasStockControl={hasStockControl}
              stockQuantity={stockQuantity}
              addtionals={additionalsAllowed}
              selections={allowedSelections}
              imageAssetIds={imageAssetIds}
              imageUrls={imageUrls}
              onImagesChange={(ids, urls) => {
                setImageAssetIds(ids)
                setImageUrls(urls)
                setImagesChanged(true)
              }}
              catalogProductEntity={combo?.catalogProductEntity}
              selectionStockTarget="combo"
              additionalStockTarget="combo"
              removeAddtionals={(id) => setAdditionalsAllowed((e) => e.filter((a) => a.id !== id))}
              addAddtionals={(id) => {
                const additional = addtionalMemory.findById(id)

                if (!additional) {
                  return
                }

                setAdditionalsAllowed((e) => (e.some((a) => a.id === id) ? e : [...e, additional]))
              }}
              addFreeAddtionals={(id) => {
                const add = additionalsAllowed.find((e) => e.id === id)

                if (add) {
                  setFreeAdditionalsAllowed((e) => (e.some((x) => x.id === id) ? e : [...e, add]))
                }
              }}
              removeFreeAddtionals={(id) => setFreeAdditionalsAllowed((e) => e.filter((a) => a.id !== id))}
              addSelections={(id) => {
                const selection = selectionMemory.findById(id)
                if (!selection) return
                setAllowedSelections((e) => (e.some((x) => x.id === id) ? e : [...e, selection]))
              }}
              removeSelections={(id) => setAllowedSelections((e) => e.filter((x) => x.id !== id))}
              handleCategoryId={handleCategoryId}
              handleTypeSelectChange={() => {}}
              onRemove={async () => {
                if (!combo) {
                  return
                }

                await handleRemoveCombo(combo, () => handleGetCombo(onClose))
              }}
              setPromotions={setPromotionsIds}
              handleTagsChange={setTags}
              handleCoastChange={handleCoastChange}
              handleDescriptionChange={handleDescriptionChange}
              handleFreeAddtionalsLimitChange={handleFreeAddtionalsLimitChange}
              handleHasStockControlChange={handleHasStockControlChange}
              handleHasPreparationTimeChange={handleHasPreparationTimeChange}
              handlePreparationTimeMinutesChange={handlePreparationTimeMinutesChange}
              handleProductOrderModeChange={handleProductOrderModeChange}
              handleReservationAllowedDaysChange={setReservationAllowedDays}
              handleReservationAllowedTimesChange={setReservationAllowedTimes}
              handleStockQuantityChange={handleStockQuantityChange}
              handleNameChange={handleNameChange}
              showCategorySelector={false}
              savePermission={Permissions.CanEditeCombos}
              onCopy={handleCopyClick}
              customSectionBeforeTags={
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="px-2 mt-1">
                    <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
                      <div className="mb-3">
                        <h3 className="font-black text-gray-800 tracking-tight">🍱 Montagem do combo</h3>

                        <p className="text-xs text-gray-500">
                          Use os componentes padrão do sistema para selecionar os itens do combo.
                        </p>
                      </div>

                      <CanView permission={Permissions.CanEditeCombos}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                            <label className="block text-xs font-bold text-slate-600 mb-2">Produtos</label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <GenericDropDown
                                  data={productOptions}
                                  disable={canEdite}
                                  initialState={selectedProductId}
                                  handlerFuncion={(event) => setSelectedProductId(event.target.value)}

                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.select.select2" />
                              </div>

                              {selectedProductId !== "0" && (
                                <ButtonIcon
                                  sizeNumber={38}
                                  icon={faPlus}
                                  disabled={canEdite}
                                  bgColor={THEME.colors.buttonBGPlusColor}
                                  iconColor={THEME.colors.buttonFGPlusColor}
                                  onClick={() => {
                                    const productId = Number(selectedProductId)

                                    if (productId > 0) {
                                      addProductItem(productId)

                                      setSelectedProductId("0")
                                    }
                                  }}

                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.button.faPlus" />
                              )}
                            </div>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                            <label className="block text-xs font-bold text-slate-600 mb-2">Itens complementares</label>

                            <div className="flex items-center gap-2">
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <GenericDropDown
                                  data={catalogItemOptions}
                                  initialState={selectedCatalogItemId}
                                  disable={canEdite}
                                  handlerFuncion={(event) => setSelectedCatalogItemId(event.target.value)}

                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.select.select3" />
                              </div>

                              {selectedCatalogItemId !== "0" && (
                                <ButtonIcon
                                  sizeNumber={38}
                                  icon={faPlus}
                                  bgColor={THEME.colors.buttonBGPlusColor}
                                  disabled={canEdite}
                                  iconColor={THEME.colors.buttonFGPlusColor}
                                  onClick={() => {
                                    const catalogItemId = Number(selectedCatalogItemId)

                                    if (catalogItemId > 0) {
                                      addCatalogItemItem(catalogItemId)
                                      setSelectedCatalogItemId("0")
                                    }
                                  }}

                  descriptionKey="interactionDescriptions:pages.products.combo.comboPopup.button.faPlus2" />
                              )}
                            </div>
                          </div>
                        </div>
                      </CanView>

                      <div className="space-y-2">
                        <CanView permission={Permissions.CanEditeCombos}>
                          {items.length > 0 && (
                            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-black text-orange-700">
                              <p>Total dos itens do combo: R$ {comboItemsTotalValue.toFixed(2).replace(".", ",")}</p>

                              <p className="text-xs font-semibold text-orange-600 mt-1">
                                Preço sugerido (total - 10%): R$ {comboSuggestedValue.toFixed(2).replace(".", ",")}
                              </p>
                            </div>
                          )}
                        </CanView>

                        {items.length === 0 && (
                          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-center text-sm text-slate-500">
                            Nenhum item adicionado ainda.
                          </div>
                        )}

                        {items.map((item, index) => {
                          const itemMeta = getItemMeta(item.itemType, item.itemId)

                          const itemTotal = itemMeta.price * item.quantity

                          return (
                            <Animate
                              key={`${item.itemType}-${item.itemId}-${index}`}
                              type={AnimationTypes.fadeInTraslate}
                              duration={`${1 + 0.4 * index}s`}
                            >
                              <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center shrink-0">
                                      {itemMeta.iconUrl ? (
                                        <img
                                          src={itemMeta.iconUrl.toClientRequest()}
                                          alt={itemMeta.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <span className="text-xl">{itemMeta.fallbackIcon}</span>
                                      )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold text-slate-700 truncate">{itemMeta.name}</p>

                                      <p className="text-[11px] text-slate-500">
                                        {item.itemType === EComboItemType.Product ? "Item de produto" : "Item de item complementar"}
                                      </p>

                                      <div className="flex flex-wrap items-center gap-2 mt-1">
                                        <span className="text-[11px] font-bold text-slate-600">
                                          Unit.: R$ {itemMeta.price.toFixed(2).replace(".", ",")}
                                        </span>

                                        <span className="text-[11px] font-black text-orange-600">
                                          Total: R$ {itemTotal.toFixed(2).replace(".", ",")}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                                    <div className="flex items-center justify-between sm:justify-start gap-2">
                                      <span className="text-xs text-slate-500 font-semibold">Qtd.</span>

                                      <div className="flex items-center gap-1">
                                        <CanView permission={Permissions.CanEditeCombos}>
                                          <button
                                            type="button"
                                            disabled={canEdite}
                                            className="w-8 h-8 rounded-md border border-slate-300 bg-slate-50 text-slate-700 font-black"
                                            onClick={() => updateQuantity(index, Math.max(1, item.quantity - 1))}

                  data-interaction-description-key="interactionDescriptions:pages.products.combo.comboPopup.button.updateQuantityIndexMathMax1ItemQuantity1">
                                            −
                                          </button>
                                        </CanView>

                                        <span className="w-10 h-8 rounded-md border border-slate-300 bg-white text-sm font-black flex items-center justify-center">
                                          {item.quantity}
                                        </span>

                                        <CanView permission={Permissions.CanEditeCombos}>
                                          <button
                                            type="button"
                                            disabled={canEdite}
                                            className="w-8 h-8 rounded-md border border-orange-300 bg-orange-50 text-orange-700 font-black"
                                            onClick={() => updateQuantity(index, item.quantity + 1)}

                  data-interaction-description-key="interactionDescriptions:pages.products.combo.comboPopup.button.updateQuantityIndexItemQuantity1">
                                            +
                                          </button>
                                        </CanView>
                                      </div>
                                    </div>

                                    <CanView permission={Permissions.CanEditeCombos}>
                                      <button
                                        type="button"
                                        disabled={canEdite}
                                        className="h-10 px-3 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-sm font-bold border border-red-200 transition-all w-full sm:w-auto"
                                        onClick={() => removeItem(index)}

                  data-interaction-description-key="interactionDescriptions:pages.products.combo.comboPopup.button.removeItemIndex">
                                        Remover
                                      </button>
                                    </CanView>
                                  </div>
                                </div>
                              </div>
                            </Animate>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </Animate>
              }
            />
          </form>
        </div>
      </div>
    </ModalPortal>
  )
}
